import { openHelp } from '../help/articles';
import { goBack } from '../../lib/router';
import { FlowApiPanel } from './FlowApiPanel';
import { useEffect, useRef, useState } from 'react';
import * as store from '../../store/ui';
import { deleteFlow, getFlow } from '../../api/flows';
import { refreshRuns } from '../../api/runs';
import { refreshAssets } from '../../api/library';
import { getProject } from '../../api/workspace';
import { refreshTable, flowBrand } from '../../api/core';
import { confirmModal } from '../../components/modal';
import { toast } from '../../lib/dom';
interface CanvasWindow extends Window { CFCanvasEmbed?: { flush(): void; whenSaved(): Promise<unknown>; whenLoaded(): Promise<unknown>; isSaved(): boolean } }

/** The Figma/React Flow implementation is archived for now. All normal Flow
 * routes use the established Drawcode canvas and its existing Graph v2 bridge. */
export function CanvasEditor({ flowId, projectId, flowName, projectName, initialAppMode = false, legacyAppMode = false }: { flowId: string; projectId: string; flowName: string; projectName: string; initialAppMode?: boolean; legacyAppMode?: boolean }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [openingDetails, setOpeningDetails] = useState(false);
  const [apiOpenRequest, setApiOpenRequest] = useState(0);
  const [conflict, setConflict] = useState<any>(null);
  const [conflictError, setConflictError] = useState('');
  const [tabMode, setTabMode] = useState<'checking' | 'editor' | 'viewer'>('checking');
  const syncTheme = () => {
    const theme = store.get().theme;
    ref.current?.contentWindow?.postMessage({ type: 'cf-canvas:theme', theme: initialAppMode && !legacyAppMode ? (theme === 'dark' ? 'dark' : 'light') : theme }, location.origin);
  };
  useEffect(() => {
    const unsubscribe = store.subscribe(syncTheme);
    return () => { unsubscribe(); };
  }, [initialAppMode, legacyAppMode]);
  useEffect(() => {
    store.touchRecent('flow', flowId);
    store.touchRecent('project', projectId);
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== location.origin || event.source !== ref.current?.contentWindow) return;
      if (event.data?.type === 'cf-canvas:help') {
        const title = typeof event.data.title === 'string' ? event.data.title.slice(0, 120) : undefined;
        const description = typeof event.data.description === 'string' ? event.data.description.slice(0, 500) : undefined;
        const flow = getFlow(flowId);
        openHelp(event.data.article, event.data.article === 'flow' ? { title: flowName, description: flow?.description } : { title, description });
      }
      if (event.data?.type === 'cf-canvas:ready') syncTheme();
      if (event.data?.type === 'cf-canvas:api') setApiOpenRequest(value => value + 1);
      if (event.data?.type === 'cf-canvas:open-app' && event.data.projectId === projectId && event.data.flowId === flowId) {
        const target = getFlow(event.data.targetFlowId);
        if (target?.project_id === projectId) location.hash = `/projects/${projectId}/flows/${target.id}?mode=app`;
      }
      if (event.data?.type === 'cf-canvas:open-canvas' && event.data.projectId === projectId && event.data.flowId === flowId) {
        location.hash = `/projects/${projectId}/flows/${flowId}`;
      }
      if (event.data?.type === 'cf-canvas:project' && event.data.projectId === projectId && event.data.flowId === flowId) {
        location.hash = `/projects/${projectId}?tab=flows`;
      }
      if (event.data?.type === 'cf-canvas:renamed' && event.data.projectId === projectId && event.data.flowId === flowId) {
        const flow = getFlow(flowId);
        if (flow) Object.assign(flow, event.data.flow);
        window.dispatchEvent(new CustomEvent('cf:data-updated', { detail:'flows' }));
      }
      if (event.data?.type === 'cf-canvas:runs-updated' && event.data.projectId === projectId && event.data.flowId === flowId) {
        void refreshRuns().catch((): void => undefined);
        void refreshAssets().catch((): void => undefined);
      }
      if (event.data?.type === 'cf-canvas:back' && event.data.projectId === projectId && event.data.flowId === flowId) goBack(`#/projects/${projectId}?tab=flows`);
      if (event.data?.type === 'cf-canvas:conflict' && event.data.projectId === projectId && event.data.flowId === flowId) setConflict(event.data.graph);
      if (event.data?.type === 'cf-canvas:delete' && event.data.projectId === projectId && event.data.flowId === flowId) {
        const flow = getFlow(flowId);
        confirmModal({
          title: `Delete ${flow?.name || flowName}?`,
          text: 'This permanently deletes the flow, its saved graph, version history, past runs and any Marketplace listing for this flow. This cannot be undone.',
          confirmLabel: 'Delete flow', danger: true,
          onConfirm: async () => {
            await deleteFlow(flowId);
            toast('Flow deleted');
            location.hash = `/projects/${projectId}?tab=flows`;
          },
        });
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      try { (ref.current?.contentWindow as CanvasWindow)?.CFCanvasEmbed?.flush(); }
      finally {
        window.removeEventListener('message', onMessage);
        const embed = (ref.current?.contentWindow as CanvasWindow)?.CFCanvasEmbed;
        void Promise.resolve(embed?.whenSaved?.()).then(() => refreshTable('asset_usages')).catch((): void => undefined);
      }
    };
  }, [flowId, projectId]);
  useEffect(() => {
    let alive = true;
    let release: (() => void) | undefined;
    const key = `bnb-flow-editor:${flowId}`;
    const finish = () => {
      const embed = (ref.current?.contentWindow as CanvasWindow)?.CFCanvasEmbed;
      embed?.flush();
      return Promise.resolve(embed?.whenSaved?.()).catch((): void => undefined).finally(() => release?.());
    };
    if (initialAppMode) {
      setTabMode('editor');
      return () => { alive = false; };
    }
    if (navigator.locks) {
      const controller = new AbortController();
      const own = async () => {
        if (!alive) return;
        setTabMode('editor');
        await new Promise<void>(resolve => { release = resolve; });
      };
      void navigator.locks.request(key, { ifAvailable: true }, lock => {
        if (lock) return own();
        if (alive) setTabMode('viewer');
      }).then(() => {
        if (alive && !release) return navigator.locks.request(key, { signal: controller.signal }, own);
      }).catch(() => { if (alive) setTabMode('viewer'); });
      return () => { alive = false; controller.abort(); finish(); };
    }
    // Older browsers use a short lease. The server revision check remains the
    // final guard if two tabs race while claiming it.
    const id = crypto.randomUUID();
    const readLease = () => { try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; } };
    const claim = () => {
      const lease = readLease();
      if (lease?.id !== id && lease?.until > Date.now()) { setTabMode('viewer'); return; }
      try {
        localStorage.setItem(key, JSON.stringify({ id, until: Date.now() + 6000 }));
        setTabMode(readLease()?.id === id ? 'editor' : 'viewer');
      } catch { setTabMode('editor'); }
    };
    claim();
    const interval = window.setInterval(claim, 2000);
    const onStorage = (event: StorageEvent) => { if (event.key === key) claim(); };
    window.addEventListener('storage', onStorage);
    return () => {
      alive = false;
      clearInterval(interval);
      window.removeEventListener('storage', onStorage);
      void finish().finally(() => { if (readLease()?.id === id) localStorage.removeItem(key); });
    };
  }, [flowId, initialAppMode]);
  const saveCopy = async () => {
    setConflictError('');
    const response = await fetch(`/api/v1/flows/${flowId}/copy`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ graph:conflict, name:flowName + ' (conflict copy)' }) });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) { setConflictError(body.error || 'Could not save the copy'); return; }
    location.hash = `/projects/${projectId}?tab=flows`;
  };
  const project = getProject(projectId);
  const query = new URLSearchParams({ embed: '1', projectId, flowId, projectName, flowName, flowDescription: getFlow(flowId)?.description || '',
    brandName:flowBrand({ project_id: projectId })?.name || '', brandSlug:flowBrand({ project_id: projectId })?.slug || '',
    workspaceId:project?.workspace_id || '', brandId:project?.brand_id || project?.data?.brand_id || '',
    ...(initialAppMode ? { screen: 'play', design: legacyAppMode ? 'v1' : 'v2' } : {}), ...(tabMode === 'viewer' ? { readOnly: '1' } : {}) });
  return <div style={{ position:'relative', display:'flex', width:'100%', minWidth:0, flex:1 }}>
    {!initialAppMode && <button className="btn" disabled={openingDetails || tabMode === 'checking'} style={{ position:'absolute', bottom:64, left:16, zIndex:4 }} onClick={async () => {
      setOpeningDetails(true);
      try {
        const embed = (ref.current?.contentWindow as CanvasWindow)?.CFCanvasEmbed;
        if (!embed) throw new Error('Wait for the editor to load.');
        await embed.whenLoaded();
        embed.flush();
        await embed.whenSaved();
        if (!embed.isSaved()) throw new Error('Could not save Flow. Resolve the saving error before opening its details.');
        await refreshTable('flows');
        location.hash = `/projects/${projectId}/flows/${flowId}/details`;
      } catch (error) {
        toast(error instanceof Error ? error.message : 'Could not open Flow details');
      } finally { setOpeningDetails(false); }
    }}>{openingDetails ? 'Saving…' : 'Flow details'}</button>}
    <FlowApiPanel flowId={flowId} openRequest={apiOpenRequest} showTrigger={!initialAppMode} beforeOpen={async () => {
      const embed = (ref.current?.contentWindow as CanvasWindow)?.CFCanvasEmbed;
      if (!embed) throw new Error('Дождитесь загрузки редактора.');
      await embed.whenLoaded();
      embed.flush();
      await embed.whenSaved();
      if (!embed.isSaved()) throw new Error('Flow не сохранён. Исправьте ошибку сохранения перед копированием curl.');
    }} />
    {tabMode === 'viewer' && <div className="banner info" role="status" style={{ position:'absolute', zIndex:5, left:16, right:16, top:16 }}>
      This flow is open for editing in another tab. You can view it here; editing will become available after that tab closes.
    </div>}
    {conflict && <div className="banner error" role="alert" style={{ position:'absolute', zIndex:5, left:16, right:16, top:16 }}>
      This flow changed in another tab. <button className="btn" onClick={() => location.reload()}>Load server version</button> <button className="btn" onClick={saveCopy}>Save local work as a copy</button>{conflictError && ` ${conflictError}`}
    </div>}
    {tabMode !== 'checking' && <iframe key={tabMode} ref={ref} data-flow-editor title={`${flowName} — Canvas`} src={'/canvas/?' + query} tabIndex={tabMode === 'viewer' ? -1 : 0} allow="clipboard-read; clipboard-write; fullscreen" allowFullScreen style={{ display:'block', width:'100%', height:'100%', minWidth:0, flex:1, border:0, pointerEvents:tabMode === 'viewer' ? 'none' : 'auto' }} />}
  </div>;
}
